import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useGetCurrentUserQuery, useGetCartQuery } from '../../redux/apis';
import { useGetUnreadCountQuery } from '../../redux/apis/endpoints/messageApi';
import { logout, checkAuth } from '../../redux/slices/authSlice';
import { selectAuthState } from '../../redux/selectors/authSelectors';
import { selectAllCartItems } from '../../redux/slices/cartSlice';
import { Bars3Icon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

// Import new components
import GlobalSearch from './GlobalSearch';
import NavItems from './NavItems';
import ActionItems, { CartIcon, MessageIcon } from './ActionItems';
import ProfileSection from './ProfileSection';
import LogoutConfirmModal from './LogoutConfirmModal';

const Nav = () => {
  const [activeItem, setActiveItem] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  
  // Check authentication status on component mount
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);
  
  const { isAuthenticated: isLoggedIn, user: userInfo } = useSelector(selectAuthState);
  
  // Get local cart state
  const allCartItems = useSelector(selectAllCartItems);
  
  const { data: user, refetch } = useGetCurrentUserQuery(undefined, {
    skip: !isLoggedIn,
  });
  
  // Add cart query to get real-time cart data
  const { data: cartData } = useGetCartQuery(undefined, {
    skip: !isLoggedIn,
  });

  // Get unread message count
  const { data: unreadCount } = useGetUnreadCountQuery(undefined, {
    skip: !isLoggedIn,
  });

  // Get the user display name - prioritize username, fall back to email or 'User'
  const userDisplayName = React.useMemo(() => {
    if (!userInfo) return 'User';
    return userInfo.username || userInfo.email || 'User';
  }, [userInfo]);

  useEffect(() => {
    const menus = [
      { include: 'browse', activeItem: 'Browse' },
      { include: 'about', activeItem: 'About' },
      { include: 'contact', activeItem: 'Contact' },
      { include: 'dashboard', activeItem: 'Dashboard' },
      { include: 'cart', activeItem: 'Cart' },
      { include: 'favorites', activeItem: 'Favorites' },
      { include: 'message', activeItem: 'Messages' }
    ];

    const pathname = location.pathname.toLowerCase();
    menus.forEach((each) => {
      if (pathname.includes(each.include.toLowerCase())) {
        setActiveItem(each.activeItem);
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    if (isLoggedIn) {
      refetch();
    }
  }, [isLoggedIn, refetch]);

  const cartNum = React.useMemo(() => {
    if (!isLoggedIn) return 0;
    
    // Use the length of the unified allCartItems array
    return allCartItems?.length || 0;

  }, [isLoggedIn, allCartItems]); // Update dependency array

  const handleItemClick = (name, link) => {
    setActiveItem(name);
    setIsMobileMenuOpen(false);
    history.push(link);
  };

  const handleSignOut = () => {
    // First dispatch logout to clear auth state
    dispatch(logout());
    
    // Then close the modal
    setShowLogoutConfirm(false);
    
    // Finally redirect to home page
    history.push('/');
  };

  // Navigation items
  const navItems = [
    { name: 'Browse', link: '/browse' },
    { name: 'About', link: '/about' },
    { name: 'Contact', link: '/contact' }
  ];

  // Action items that will be shown in both desktop and mobile menu when logged in
  const actionItems = isLoggedIn ? [
    {
      name: 'Cart',
      icon: <CartIcon cartCount={cartNum} />,
      link: '/cart'
    },
    {
      name: 'Messages',
      icon: <MessageIcon unreadCount={unreadCount || 0} />,
      link: '/message'
    },
    {
      name: 'Favorites',
      icon: <div className="relative"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg></div>,
      link: '/favorites'
    }
  ] : [];

  return (
    <>
      <nav className="bg-brand-teal">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link to="/" className="text-white font-bold text-xl hover:text-white">
                  SHEYOU
                </Link>
              </div>
              
              {/* Mobile menu button */}
              <div className="md:hidden ml-4">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-full text-white hover:bg-brand-teal-light hover:text-white"
                  aria-label="Open menu"
                >
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>

              {/* Desktop navigation */}
              <div className="hidden md:block ml-10">
                <div className="flex items-center space-x-4">
                  <NavItems 
                    navItems={navItems}
                    activeItem={activeItem}
                    handleItemClick={handleItemClick}
                  />
                </div>
              </div>
            </div>

            {/* Search bar in the middle (desktop only) */}
            <div className="hidden md:block">
              <GlobalSearch />
            </div>

            {/* Desktop action items */}
            <div className="hidden md:flex items-center space-x-4">
              {isLoggedIn ? (
                <>
                  <ActionItems 
                    items={actionItems} 
                    activeItem={activeItem} 
                  />
                  <ProfileSection 
                    user={user} 
                    setShowLogoutConfirm={setShowLogoutConfirm} 
                    activeItem={activeItem}
                  />
                </>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-orange hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange hover:text-white"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Mobile search */}
              <div className="px-2 pb-2 pt-1">
                <GlobalSearch />
              </div>
              
              {/* Navigation Items */}
              <NavItems 
                navItems={navItems}
                activeItem={activeItem}
                handleItemClick={handleItemClick}
                isMobile={true}
              />

              {/* Action Items */}
              {isLoggedIn && (
                <div className="pt-4 pb-3 border-t border-brand-teal">
                  <div className="px-4 py-2 flex items-center">
                    <div className="mr-3">
                      <ProfileSection 
                        user={user} 
                        setShowLogoutConfirm={setShowLogoutConfirm}
                        isMobile={true}
                      />
                    </div>
                    <div className="text-base font-medium text-white">{userDisplayName}</div>
                  </div>
                  <div className="mt-3 px-2 space-y-1">
                    <ActionItems 
                      items={actionItems} 
                      activeItem={activeItem}
                      isMobile={true}
                    />
                    
                    {/* Add direct link to Dashboard */}
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center w-full px-4 py-2 text-sm text-white rounded-md
                        ${activeItem === 'Dashboard' 
                          ? 'bg-brand-teal-dark' 
                          : 'hover:bg-brand-teal-light'
                        }`}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Dashboard
                    </Link>
                    
                    {/* Sign Out button */}
                    <button
                      onClick={() => {
                        setShowLogoutConfirm(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-brand-teal-light rounded-md"
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}

              {/* Sign In button for mobile when not logged in */}
              {!isLoggedIn && (
                <div className="px-5 py-4">
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-orange hover:bg-brand-orange-light"
                  >
                    Sign In
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Logout confirmation modal */}
      <LogoutConfirmModal 
        showLogoutConfirm={showLogoutConfirm} 
        setShowLogoutConfirm={setShowLogoutConfirm} 
        handleSignOut={handleSignOut} 
      />
    </>
  );
};

export default Nav; 